import React, { useCallback, useEffect, useState } from 'react'

import GradientButton from '../../components/GradientButton'
import { useT } from '../../i18n'
import type { Lot } from '../../types/lot'
import { inventoryApi } from '../../utils/inventoryApi'
import { farmTimeZone, formatFarmDateTime } from '../../utils/farmTime'
import { activeWithdrawal, treatmentApi, type TreatmentRecord } from '../../utils/treatmentApi'
import { TREATMENT_METHOD_KEY } from './LotTreatmentDialog'

// Tratamientos v0 (findings #103): the lot's treatments, newest first, with the
// withdrawal period still running on top. Delete asks for a reason and keeps
// the record (shown under "Eliminados").

export function WithdrawalWarning({ lotId, atDate }: { lotId: string; atDate?: string }) {
  const t = useT()
  const [running, setRunning] = useState<TreatmentRecord | null>(null)
  useEffect(() => {
    let cancelled = false
    const at = atDate && /^\d{4}-\d{2}-\d{2}$/.test(atDate) ? `${atDate}T12:00:00.000Z` : new Date().toISOString()
    void treatmentApi.list({ lotId }).then((rows) => {
      if (!cancelled) setRunning(activeWithdrawal(rows, at))
    }).catch(() => {
      if (!cancelled) setRunning(null)
    })
    return () => {
      cancelled = true
    }
  }, [lotId, atDate])
  if (!running?.withdrawalEndsAt) return null
  return (
    <div role="alert" data-testid="lot-withdrawal-warning" style={{ padding: '8px 12px', borderRadius: 8, background: '#fef2f2', color: '#991b1b', fontSize: 13, fontWeight: 600 }}>
      {t('lots.treatments.withdrawalWarning', {
        product: running.productName,
        until: formatFarmDateTime(running.withdrawalEndsAt, farmTimeZone(running.farmId))
      })}
    </div>
  )
}

export default function LotTreatmentsSection({ lot, canEdit, refreshKey, onRecord }: {
  lot: Lot
  canEdit: boolean
  refreshKey: number
  onRecord: () => void
}) {
  const t = useT()
  const [rows, setRows] = useState<TreatmentRecord[]>([])
  const [error, setError] = useState<string | null>(null)
  const [deletingId, setDeletingId] = useState<string | null>(null)
  const [deleteReason, setDeleteReason] = useState('')
  const timeZone = farmTimeZone(lot.farmId)

  const load = useCallback(async () => {
    try {
      setRows(await treatmentApi.list({ lotId: lot.id, includeDeleted: true }))
      setError(null)
    } catch {
      setError(t('lots.treatments.loadError'))
    }
  }, [lot.id, t])

  useEffect(() => {
    void load()
  }, [load, refreshKey])

  const remove = async (row: TreatmentRecord) => {
    if (!deleteReason.trim()) return
    try {
      const items = await inventoryApi.listAll()
      await treatmentApi.remove(row, deleteReason.trim(), items)
      setDeletingId(null)
      setDeleteReason('')
      await load()
    } catch {
      setError(t('lots.treatments.error.save'))
    }
  }

  const active = rows.filter((row) => !row.deletedAt)
  const deleted = rows.filter((row) => row.deletedAt)
  const running = activeWithdrawal(active, new Date().toISOString())

  const renderRow = (row: TreatmentRecord) => (
    <li key={row.id} data-testid={`lot-treatment-${row.id}`} style={{ padding: 10, border: '1px solid #e2e8f0', borderRadius: 8, display: 'grid', gap: 4, fontSize: 13, opacity: row.deletedAt ? 0.6 : 1 }}>
      <div style={{ display: 'flex', justifyContent: 'space-between', gap: 8, flexWrap: 'wrap' }}>
        <strong>{formatFarmDateTime(row.appliedAt, timeZone)} · {t(TREATMENT_METHOD_KEY[row.method] ?? TREATMENT_METHOD_KEY.other)} · {row.productName}</strong>
        {canEdit && !row.deletedAt && deletingId !== row.id ? (
          <GradientButton size="xs" variant="secondary" onClick={() => { setDeletingId(row.id); setDeleteReason('') }}>{t('common.delete')}</GradientButton>
        ) : null}
      </div>
      <span>
        {[
          row.dose ? t('lots.treatments.row.dose', { dose: row.dose }) : null,
          row.quantityUsed !== null ? t('lots.treatments.row.quantity', { quantity: String(row.quantityUsed), unit: row.quantityUnit ?? '' }) : null,
          row.durationMinutes !== null ? t('lots.treatments.row.duration', { minutes: row.durationMinutes }) : null
        ].filter(Boolean).join(' · ')}
      </span>
      <span>{t('lots.treatments.row.reason', { reason: row.reason })}{row.prescribedBy ? ` · ${t('lots.treatments.row.prescribedBy', { name: row.prescribedBy })}` : ''}</span>
      <span>{row.withdrawalEndsAt
        ? t('lots.treatments.row.withdrawal', { days: row.withdrawalDays, until: formatFarmDateTime(row.withdrawalEndsAt, timeZone) })
        : t('lots.treatments.row.noWithdrawal')}</span>
      {row.notes ? <span style={{ color: '#475569' }}>{row.notes}</span> : null}
      {row.deletedAt ? <span style={{ color: '#991b1b' }}>{t('lots.treatments.row.deleted', { reason: row.deletedReason ?? '' })}</span> : null}
      {deletingId === row.id ? (
        <div style={{ display: 'flex', gap: 6, flexWrap: 'wrap', alignItems: 'center' }}>
          <input
            value={deleteReason}
            onChange={(event) => setDeleteReason(event.target.value)}
            placeholder={t('lots.treatments.deleteReason')}
            style={{ padding: '6px 8px', border: '1px solid #cbd5e1', borderRadius: 8, flex: '1 1 200px' }}
          />
          <GradientButton size="xs" variant="danger" disabled={!deleteReason.trim()} onClick={() => void remove(row)}>{t('common.delete')}</GradientButton>
          <GradientButton size="xs" variant="secondary" onClick={() => setDeletingId(null)}>{t('common.cancel')}</GradientButton>
        </div>
      ) : null}
    </li>
  )

  return (
    <div data-testid="lots-detail-treatments" style={{ display: 'grid', gap: 10 }}>
      <div style={{ display: 'flex', justifyContent: 'space-between', gap: 8, flexWrap: 'wrap', alignItems: 'center' }}>
        <strong>{t('lots.treatments.title')}</strong>
        {canEdit ? <GradientButton size="xs" variant="info" onClick={onRecord} data-testid="lots-detail-treatments-add">{t('lots.treatments.add')}</GradientButton> : null}
      </div>
      {running?.withdrawalEndsAt ? (
        <div role="status" style={{ padding: '8px 12px', borderRadius: 8, background: '#fef2f2', color: '#991b1b', fontSize: 13, fontWeight: 600 }}>
          {t('lots.treatments.withdrawalRunning', { until: formatFarmDateTime(running.withdrawalEndsAt, timeZone) })}
        </div>
      ) : null}
      {error ? <div role="alert" style={{ color: '#b91c1c', fontSize: 13 }}>{error}</div> : null}
      {active.length === 0 ? <div style={{ fontSize: 13, color: '#64748b' }}>{t('lots.treatments.empty')}</div> : (
        <ul style={{ listStyle: 'none', padding: 0, margin: 0, display: 'grid', gap: 6 }}>{active.map(renderRow)}</ul>
      )}
      {deleted.length > 0 ? (
        <details>
          <summary style={{ fontSize: 12, color: '#64748b', cursor: 'pointer' }}>{t('lots.treatments.deletedList', { count: deleted.length })}</summary>
          <ul style={{ listStyle: 'none', padding: 0, margin: '6px 0 0', display: 'grid', gap: 6 }}>{deleted.map(renderRow)}</ul>
        </details>
      ) : null}
    </div>
  )
}
